import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Target, Lightbulb, Trophy } from 'lucide-react';
import './ProjectsPage.css';

const ProjectsPage = () => {
  const [filter, setFilter] = useState('All');

  const projects = [
    {
      id: 1,
      name: 'Nexus Analytics',
      category: 'Fintech',
      problem: 'Startups struggled to track complex financial metrics across multiple fragmented payment platforms.',
      solution: 'A unified dashboard that aggregates data from Stripe, PayPal, and banks into a single real-time source of truth.',
      outcome: 'Reduced reporting time by 80% and helped 50+ startups secure their next funding round.',
      stack: ['Next.js', 'PostgreSQL', 'Stripe API'],
      color: '59, 130, 246',
      icon: '📊'
    },
    {
      id: 2,
      name: 'EcoFlow Supply',
      category: 'Logistics',
      problem: 'High waste and inefficient routing in organic supply chains were draining margins and increasing carbon footprints.',
      solution: 'An AI-powered route optimization engine with real-time tracking for perishable goods.',
      outcome: '15% reduction in fuel costs and zero reported spoilage over 12 months of operation.',
      stack: ['React', 'Node.js', 'Google Maps API'],
      color: '16, 185, 129',
      icon: '🌱'
    },
    {
      id: 3,
      name: 'Streamline ERP',
      category: 'Manufacturing',
      problem: 'Manual inventory management led to frequent production delays and overstocking issues.',
      solution: 'Automated inventory tracking system with predictive reordering based on historical demand.',
      outcome: '40% increase in production efficiency and a $200k reduction in annual overstock waste.',
      stack: ['Vue.js', 'Python', 'AWS'],
      color: '245, 158, 11',
      icon: '⚙️'
    },
    {
      id: 4,
      name: 'Aether AI Chat',
      category: 'AI Solutions',
      problem: 'Customer support teams were overwhelmed by repetitive queries, leading to high response times.',
      solution: 'Custom-trained LLM chatbot integrated with company knowledge base for instant, accurate support.',
      outcome: '70% reduction in support ticket volume and 95% customer satisfaction rate.',
      stack: ['Python', 'OpenAI', 'LangChain'],
      color: '139, 92, 246',
      icon: '🧠'
    },
    {
      id: 5,
      name: 'Pulse Health',
      category: 'Healthcare',
      problem: 'Patient data was siloed, making it difficult for doctors to get a holistic view of patient history.',
      solution: 'Secure, HIPAA-compliant patient portal with real-time health monitoring integration.',
      outcome: 'Improved diagnosis accuracy and 30% faster patient onboarding.',
      stack: ['React Native', 'Node.js', 'MongoDB'],
      color: '236, 72, 153',
      icon: '❤️'
    },
    {
      id: 6,
      name: 'Horizon Real Estate',
      category: 'PropTech',
      problem: 'Property buyers found it difficult to visualize off-plan properties virtually.',
      solution: 'Interactive 3D property viewing platform with real-time availability tracking.',
      outcome: '50% increase in off-plan sales and enhanced buyer engagement.',
      stack: ['Three.js', 'React', 'Firebase'],
      color: '99, 102, 241',
      icon: '🏠'
    }
  ];

  const categories = ['All', ...new Set(projects.map(p => p.category))];

  const filteredProjects = filter === 'All' 
    ? projects 
    : projects.filter(p => p.category === filter);

  return (
    <motion.div 
      className="projects-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <section className="projects-hero">
        <div className="container">
          <motion.h1 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
          >
            Engineering <span>Impactful</span> Solutions
          </motion.h1>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            A collection of technical challenges we've solved for businesses worldwide.
          </motion.p>
        </div>
      </section>

      <section className="projects-grid-section">
        <div className="container">
          <div className="filter-bar">
            {categories.map(cat => (
              <button 
                key={cat}
                className={`filter-btn ${filter === cat ? 'active' : ''}`}
                onClick={() => setFilter(cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          <motion.div 
            className="projects-detailed-grid"
            layout
          >
            <AnimatePresence>
              {filteredProjects.map((project) => (
                <motion.div 
                  key={project.id}
                  className="project-card-premium"
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="pc-preview" style={{ background: `rgb(${project.color})` }}>
                    <span className="pc-icon-large">{project.icon}</span>
                    <div className="pc-overlay">
                      <button className="view-case-btn">
                        View Case Study <ArrowRight size={16} />
                      </button>
                    </div>
                  </div>
                  <div className="pc-content">
                    <div className="pc-top">
                      <span className="pc-category">{project.category}</span>
                      <h3>{project.name}</h3>
                    </div>
                    
                    <div className="pc-brief-sections">
                      <div className="pc-brief-item">
                        <Target size={14} className="item-icon" />
                        <div>
                          <strong>Problem:</strong> {project.problem}
                        </div>
                      </div>
                      <div className="pc-brief-item">
                        <Lightbulb size={14} className="item-icon" />
                        <div>
                          <strong>Solution:</strong> {project.solution}
                        </div>
                      </div>
                      <div className="pc-brief-item">
                        <Trophy size={14} className="item-icon" />
                        <div>
                          <strong>Outcome:</strong> {project.outcome}
                        </div>
                      </div>
                    </div>

                    <div className="pc-footer">
                      <div className="pc-stack">
                        {project.stack.map((s, i) => (
                          <span key={i} className="pc-stack-pill">{s}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default ProjectsPage;
